import React, { PropsWithChildren, useEffect, useState } from "react";
import { DefaultTheme as DefaultTheme, ThemeProvider } from "styled-components";
import { dark, light, ThemeNames } from "../theme";

const localStorageKey = "theme";

type Dispatch = (theme: DefaultTheme) => void;

type State = {
  theme: DefaultTheme;
};

const getThemeFromLocalStorage = (): DefaultTheme | undefined => {
  const storedTheme = localStorage.getItem(localStorageKey);
  if (storedTheme !== null) {
    if (storedTheme === light.name) {
      return light;
    }
    return dark;
  }
  return undefined;
};

const getDefaultTheme = (): DefaultTheme => {
  const storedTheme = getThemeFromLocalStorage();
  return storedTheme ? storedTheme : light;
};

export const ThemeContext = React.createContext<State>({
  theme: dark,
});

export const ThemeDispatchContext = React.createContext<Dispatch | undefined>(
  undefined,
);

const themeReducer = (state: State, theme: DefaultTheme) => {
  localStorage.setItem(localStorageKey, theme.name);
  return { theme: theme };
};

const ScadaPlusThemeProvider: React.FC<PropsWithChildren> = (props) => {
  const [state, dispatch] = React.useReducer(themeReducer, {
    theme: getDefaultTheme(),
  });

  const [isDarkMode, setIsDarkMode] = useState(
    () =>
      localStorage.getItem("theme") === "dark" ||
      (!localStorage.getItem("theme") &&
        window.matchMedia("(prefers-color-scheme: dark)").matches),
  );

  useEffect(() => {
    // Add or remove the `dark` class on the root element
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  useEffect(() => {
    setIsDarkMode(state.theme.name === ThemeNames.Dark);
  }, [state]);

  return (
    <ThemeContext.Provider value={state}>
      <ThemeProvider theme={state.theme}>
        <ThemeDispatchContext.Provider value={dispatch}>
          {props.children}
        </ThemeDispatchContext.Provider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export { ScadaPlusThemeProvider };
